import React, { useState, useEffect, useCallback } from 'react';
import { Heart, MessageCircle, Share2, X, Loader2, Link as LinkIcon, ExternalLink, Play } from 'lucide-react';
import { toast } from 'sonner';

const API = window.location.origin + '/api';

const PLATFORM_META = {
  youtube: { label: 'YouTube', color: 'bg-red-500/10 text-red-600 border-red-200' },
  tiktok: { label: 'TikTok', color: 'bg-gray-900/10 text-gray-900 border-gray-300' },
  instagram: { label: 'Instagram', color: 'bg-pink-500/10 text-pink-600 border-pink-200' },
  facebook: { label: 'Facebook', color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
  other: { label: 'Link', color: 'bg-gray-100 text-gray-600 border-gray-200' },
};

// Small strip rendered under each platform embed. Cross-origin iframes don't reliably
// fire onError when content is blocked ("video unavailable"), so we always render this
// fallback link as an escape hatch — users can tap to open the original on the platform.
function EmbedFallbackBar({ platform, onOpen, testid }) {
  return (
    <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-2 text-[11px]">
      <span className="text-gray-500">Video not loading?</span>
      <button
        type="button"
        onClick={onOpen}
        data-testid={testid}
        className="inline-flex items-center gap-1 font-bold text-bundle-green hover:text-bundle-green-dark"
      >
        View on {platform} <ExternalLink className="w-3 h-3" />
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// SHARE GOLF LINK MODAL — paste URL → preview → optional caption → post
// ────────────────────────────────────────────────────────────────────────
export function ShareContentModal({ onClose, onPosted }) {
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const fetchPreview = useCallback(async (u) => {
    if (!u || !/^https?:\/\//.test(u)) { setPreview(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/shared/parse-url`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: u }),
      });
      if (res.ok) setPreview(await res.json());
      else setPreview(null);
    } catch (e) {
      console.error('preview failed', e);
      setPreview(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchPreview(url), 600);
    return () => clearTimeout(t);
  }, [url, fetchPreview]);

  const submit = async () => {
    if (!url) return toast.error('Paste a link first');
    if (!preview) return toast.error('Could not preview that link');
    setPosting(true);
    try {
      const res = await fetch(`${API}/shared/post`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, caption, title: preview.title, thumbnail: preview.thumbnail, platform: preview.platform, video_id: preview.video_id }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success('Shared to the feed');
        onPosted?.(d.post);
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        // FastAPI 422 returns detail as an array of validation errors
        const msg = typeof err.detail === 'string'
          ? err.detail
          : Array.isArray(err.detail) && err.detail[0]?.msg
            ? `Invalid: ${err.detail[0].msg}`
            : `Failed to post (${res.status})`;
        toast.error(msg);
      }
    } catch (e) {
      console.error('share post failed', e);
      toast.error('Network error — please try again');
    } finally { setPosting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="share-content-modal">
        <div className="sticky top-0 bg-bundle-green text-white px-4 py-3 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            <h2 className="font-heading font-bold text-base">Post a Golf Link</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="share-content-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="bg-bundle-cream/50 rounded-lg p-2.5 text-[11px] text-gray-600 leading-relaxed space-y-1.5">
            <p>
              <strong className="text-bundle-green">How to:</strong> In YouTube/TikTok/Instagram/Facebook, tap <strong>Share → Copy Link</strong>, then come back here and tap Paste.
            </p>
            <p className="border-t border-bundle-cream-dark pt-1.5">
              <strong className="text-amber-700">Heads up:</strong> mobile <em>"Share" shortcuts</em> often paste short redirect URLs that won't embed (you'll see "video unavailable"). For best results, copy the <strong>direct video URL from the post's page</strong>:
            </p>
            <ul className="list-disc pl-4 text-[10px] text-gray-600 space-y-0.5">
              <li><strong>Facebook:</strong> open the video, then copy the URL from your browser's address bar.</li>
              <li><strong>Instagram / TikTok:</strong> open the post on the web, copy the canonical URL (looks like <code>instagram.com/reel/...</code> or <code>tiktok.com/@user/video/...</code>).</li>
            </ul>
          </div>

          <label className="block">
            <span className="block text-xs font-semibold text-gray-700 mb-1">Paste a YouTube, TikTok, Instagram or Facebook link</span>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <LinkIcon className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onPaste={(e) => {
                    const text = e.clipboardData?.getData('text');
                    if (text) { e.preventDefault(); setUrl(text.trim()); }
                  }}
                  placeholder="https://…"
                  autoFocus
                  data-testid="share-url-input"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-bundle-green focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    if (navigator.clipboard?.readText) {
                      const text = (await navigator.clipboard.readText() || '').trim();
                      if (!text) { toast.error('Clipboard is empty — copy a link first'); return; }
                      if (!/^https?:\/\//i.test(text)) {
                        toast.error(`That's not a link. Copy a YouTube, TikTok, Instagram or Facebook URL first.`);
                        return;
                      }
                      setUrl(text);
                      toast.success('Pasted');
                      return;
                    }
                    toast.error('Long-press the box to paste');
                  } catch (e) {
                    console.error('clipboard read failed', e);
                    toast.error('Paste blocked — long-press the box instead');
                  }
                }}
                data-testid="share-paste-btn"
                className="flex-shrink-0 px-3 py-2 bg-bundle-green hover:bg-bundle-green-dark text-white rounded-lg text-xs font-bold whitespace-nowrap"
              >
                Paste
              </button>
            </div>
          </label>

          {loading && (
            <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating preview…
            </div>
          )}

          {!loading && url && /^https?:\/\//i.test(url) && !preview && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <strong>Couldn't preview that link.</strong> Make sure it's a valid YouTube, TikTok, Instagram, or Facebook URL. Private or deleted posts won't load.
            </div>
          )}

          {!loading && url && !/^https?:\/\//i.test(url) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
              That doesn't look like a link. Paste a full URL starting with <code className="font-mono">https://</code>
            </div>
          )}

          {preview && !loading && (() => {
            // Live embed in the preview matches what the feed card will render,
            // avoiding "blank thumbnail" issues when IG/FB block hotlinked og:image.
            const plat = preview.platform;
            const vid = preview.video_id;
            const embedSrc =
              plat === 'youtube' && vid ? `https://www.youtube.com/embed/${vid}?rel=0` :
              plat === 'instagram' && vid ? `https://www.instagram.com/p/${vid}/embed/captioned/` :
              plat === 'tiktok' && vid ? `https://www.tiktok.com/embed/v2/${vid}` :
              plat === 'facebook' ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(vid || url)}&show_text=false&width=560` :
              null;
            const embedHeight = plat === 'instagram' ? 520 : plat === 'tiktok' ? 660 : plat === 'facebook' ? 420 : 220;
            return (
              <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="share-preview">
                {embedSrc ? (
                  <div className="relative w-full bg-black">
                    <iframe
                      src={embedSrc}
                      title={preview.title || 'Preview'}
                      allow="encrypted-media; picture-in-picture; web-share; autoplay"
                      allowFullScreen
                      scrolling="no"
                      frameBorder="0"
                      className="w-full"
                      style={{ height: embedHeight, border: 0 }}
                      data-testid={`share-preview-iframe-${plat}`}
                    />
                    <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${PLATFORM_META[plat]?.color || PLATFORM_META.other.color}`}>
                      {PLATFORM_META[plat]?.label || 'Link'}
                    </span>
                  </div>
                ) : preview.thumbnail ? (
                  <div className="relative w-full aspect-video bg-gray-100">
                    <img src={preview.thumbnail} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${PLATFORM_META[plat]?.color || PLATFORM_META.other.color}`}>
                      {PLATFORM_META[plat]?.label || 'Link'}
                    </span>
                  </div>
                ) : null}
                <div className="p-3">
                  <p className="text-sm font-bold text-gray-900 line-clamp-2">{preview.title}</p>
                  {preview.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{preview.description}</p>}
                </div>
              </div>
            );
          })()}

          <label className="block">
            <span className="block text-xs font-semibold text-gray-700 mb-1">Caption (optional)</span>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a note (optional)"
              maxLength={500}
              rows={3}
              data-testid="share-caption-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-bundle-green focus:outline-none resize-none"
            />
            <p className="text-[10px] text-gray-400 mt-0.5 text-right">{caption.length}/500</p>
          </label>

          <button
            type="button"
            onClick={submit}
            disabled={posting || !preview}
            data-testid="share-submit-btn"
            className="w-full bg-bundle-green hover:bg-bundle-green-dark disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2"
          >
            {posting ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting…</> : <><Share2 className="w-4 h-4" /> Post to Feed</>}
          </button>
          <p className="text-[10px] text-gray-400 text-center">Keep it golf-related. 5 shares per day.</p>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// SHARED CONTENT CARD — renders inside the main feed
// ────────────────────────────────────────────────────────────────────────
export function SharedContentCard({ post, user, onLike, onOpenComments, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const isLiked = post.likes?.includes(user?.user_id);
  const isOwner = user?.user_id === post.user_id;

  // Client-side platform detection fallback — handles old posts where backend
  // didn't recognize the URL (e.g., posts created before Facebook support shipped)
  const detectPlatform = () => {
    if (post.platform && post.platform !== 'other') return post.platform;
    const u = post.shared_url || '';
    if (/youtube\.com|youtu\.be/i.test(u)) return 'youtube';
    if (/tiktok\.com/i.test(u)) return 'tiktok';
    if (/instagram\.com/i.test(u)) return 'instagram';
    if (/facebook\.com|fb\.watch|fb\.com/i.test(u)) return 'facebook';
    return 'other';
  };
  const effectivePlatform = detectPlatform();
  const platformMeta = PLATFORM_META[effectivePlatform] || PLATFORM_META.other;

  // Fallback: extract video_id client-side if backend didn't (e.g., older posts)
  const extractVideoId = () => {
    if (post.video_id) return post.video_id;
    const url = post.shared_url || '';
    if (effectivePlatform === 'youtube') {
      const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
      return m ? m[1] : null;
    }
    if (effectivePlatform === 'instagram') {
      const m = url.match(/instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i);
      return m ? m[1] : null;
    }
    if (effectivePlatform === 'tiktok') {
      const m = url.match(/tiktok\.com\/(?:@[^/]+\/)?video\/(\d+)/i) || url.match(/tiktok\.com\/v\/(\d+)/i);
      return m ? m[1] : null;
    }
    if (effectivePlatform === 'facebook') {
      return url; // FB embed uses full URL, not an ID
    }
    return null;
  };
  const videoId = extractVideoId();

  const timeAgo = (iso) => {
    if (!iso) return '';
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return 'Just now';
    if (d < 3600) return `${Math.floor(d / 60)}m`;
    if (d < 86400) return `${Math.floor(d / 3600)}h`;
    return `${Math.floor(d / 86400)}d`;
  };

  const openOriginal = () => window.open(post.shared_url, '_blank', 'noopener,noreferrer');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-bundle-cream-dark overflow-hidden" data-testid={`shared-card-${post.post_id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-2">
        <img src={post.user_picture || '/default-avatar.png'} alt="" className="w-9 h-9 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm truncate">{post.user_name}</p>
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
            <span>shared a {platformMeta.label.toLowerCase()}</span>
            <span>·</span>
            <span>{timeAgo(post.created_at)} ago</span>
          </div>
        </div>
      </div>

      {/* Caption */}
      {post.caption && <p className="px-4 pb-2 text-sm text-gray-700">{post.caption}</p>}

      {/* Media */}
      {effectivePlatform === 'youtube' && videoId ? (
        expanded ? (
          <div className="relative w-full aspect-video bg-black">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={post.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
              data-testid={`shared-yt-iframe-${post.post_id}`}
            />
          </div>
        ) : (
          <button type="button" onClick={() => setExpanded(true)} className="relative w-full aspect-video bg-black block group" data-testid={`shared-yt-play-${post.post_id}`}>
            {post.thumbnail_url && <img src={post.thumbnail_url} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-red-600/90 group-hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
                <Play className="w-7 h-7 text-white fill-current ml-1" />
              </div>
            </div>
            <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${platformMeta.color}`}>{platformMeta.label}</span>
          </button>
        )
      ) : effectivePlatform === 'instagram' && videoId ? (
        <>
          <div className="relative w-full bg-black" style={{ minHeight: 480 }}>
            <iframe
              src={`https://www.instagram.com/p/${videoId}/embed/captioned/`}
              title={post.title || 'Instagram post'}
              allow="encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              scrolling="no"
              frameBorder="0"
              className="w-full"
              style={{ height: 580, border: 0 }}
              data-testid={`shared-ig-iframe-${post.post_id}`}
            />
          </div>
          <EmbedFallbackBar platform="Instagram" onOpen={openOriginal} testid={`shared-ig-fallback-${post.post_id}`} />
        </>
      ) : effectivePlatform === 'tiktok' && videoId ? (
        <>
          <div className="relative w-full bg-black" style={{ minHeight: 580 }}>
            <iframe
              src={`https://www.tiktok.com/embed/v2/${videoId}`}
              title={post.title || 'TikTok video'}
              allow="encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              scrolling="no"
              frameBorder="0"
              className="w-full"
              style={{ height: 740, border: 0 }}
              data-testid={`shared-tt-iframe-${post.post_id}`}
            />
          </div>
          <EmbedFallbackBar platform="TikTok" onOpen={openOriginal} testid={`shared-tt-fallback-${post.post_id}`} />
        </>
      ) : post.platform === 'facebook' ? (
        <>
          <div className="relative w-full bg-black" style={{ minHeight: 480 }}>
            <iframe
              src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(post.video_id || post.shared_url)}&show_text=false&width=560`}
              title={post.title || 'Facebook video'}
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              allowFullScreen
              scrolling="no"
              frameBorder="0"
              className="w-full"
              style={{ height: 540, border: 0 }}
              data-testid={`shared-fb-iframe-${post.post_id}`}
            />
          </div>
          <EmbedFallbackBar platform="Facebook" onOpen={openOriginal} testid={`shared-fb-fallback-${post.post_id}`} />
        </>
      ) : (
        <button type="button" onClick={openOriginal} className="block w-full text-left" data-testid={`shared-link-open-${post.post_id}`}>
          {post.thumbnail_url ? (
            <div className="relative w-full aspect-video bg-gray-100 group overflow-hidden">
              <img src={post.thumbnail_url} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${platformMeta.color}`}>{platformMeta.label}</span>
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Open
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 bg-gray-50 border-y border-gray-200 flex items-center gap-2 text-xs text-gray-600">
              <ExternalLink className="w-4 h-4" /> {post.shared_url}
            </div>
          )}
        </button>
      )}

      {/* Title */}
      {post.title && (
        <div className="px-4 py-2">
          <p className="text-sm font-semibold text-gray-900 line-clamp-2">{post.title}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-t border-bundle-cream-dark">
        <button onClick={() => onLike?.(post)} className={`flex items-center gap-1.5 ${isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`} data-testid={`shared-like-${post.post_id}`}>
          <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
          <span className="text-xs font-semibold">{post.likes?.length || 0}</span>
        </button>
        <button onClick={() => onOpenComments?.(post)} className="flex items-center gap-1.5 text-gray-500 hover:text-bundle-green" data-testid={`shared-comment-${post.post_id}`}>
          <MessageCircle className="w-5 h-5" />
          <span className="text-xs font-semibold">{post.comment_count || post.comments_count || 0}</span>
        </button>
        <button onClick={openOriginal} className="ml-auto text-gray-400 hover:text-bundle-green" title="Open original" data-testid={`shared-open-${post.post_id}`}>
          <ExternalLink className="w-4 h-4" />
        </button>
        {(isOwner || user?.is_admin) && (
          <button onClick={() => onDelete?.(post)} className="text-gray-400 hover:text-red-500 text-xs" data-testid={`shared-delete-${post.post_id}`}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
